import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Mail, Receipt, Lock, Save, CheckCircle, XCircle, Loader2, ChevronRight, ChevronDown, ShieldCheck } from 'lucide-react';
import { Skeleton } from '../ui/kit';

// ─────────────────────────────────────────────────────────────────────────────
// Mail-Konten — Support (Empfang IMAP + Versand SMTP) und Rechnung (Versand).
// E-Mail + Passwort im Admin pflegen statt per .env/Code. Passwörter liegen VERSCHLÜSSELT
// in der DB und werden NIE zurückgeladen (Feld bleibt leer = „behalten"). Live-Testknopf.
// Backend: GET/POST /admin/mail-accounts, POST /admin/mail-accounts/test.
// ─────────────────────────────────────────────────────────────────────────────

const inp = { width: '100%', padding: '9px 12px', background: '#e5e7eb', border: '1.5px solid #d1d5db', borderRadius: 9, color: '#0a0a0a', fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
const smallInp = { ...inp, padding: '7px 10px', fontSize: 12 };
const btn = (color = '#0a0a0a', extra = {}) => ({ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: color, color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', ...extra });
const cardStyle = { background: '#ffffff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 24, marginBottom: 20 };
const lbl = { fontSize: 12, fontWeight: 700, color: '#4b5563', marginBottom: 5, display: 'block' };

function Field({ label, ...rest }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={lbl}>{label}</span>
      <input style={inp} {...rest} />
    </label>
  );
}

function AccountCard({ meta, info, onSaved }) {
  const [draft, setDraft] = useState({ user: '', password: '', from: '', smtp_host: '', smtp_port: '', imap_host: '', imap_port: '' });
  const [adv, setAdv] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setDraft({
      user: info.user || '', password: '', from: info.from || '',
      smtp_host: info.smtp_host || '', smtp_port: String(info.smtp_port || ''),
      imap_host: info.imap_host || '', imap_port: String(info.imap_port || ''),
    });
  }, [info]);

  const set = (k) => (e) => setDraft(d => ({ ...d, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setResult(null);
    try {
      const body = { account: meta.key, user: draft.user, from: draft.from,
        smtp_host: draft.smtp_host, smtp_port: draft.smtp_port };
      if (meta.imap) { body.imap_host = draft.imap_host; body.imap_port = draft.imap_port; }
      if (draft.password) body.password = draft.password;   // leer = Passwort behalten
      await api.post('/admin/mail-accounts', body);
      toast.success(`${meta.title} gespeichert`);
      setDraft(d => ({ ...d, password: '' }));
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const { data } = await api.post('/admin/mail-accounts/test', { account: meta.key });
      setResult(data);
    } catch (e) { setResult({ ok: false, detail: e.response?.data?.detail || e.message }); }
    finally { setTesting(false); }
  };

  const Icon = meta.icon;
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${meta.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={20} color={meta.color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a' }}>{meta.title}</div>
          <div style={{ fontSize: 12, color: '#4b5563' }}>{meta.sub}</div>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 9px', borderRadius: 999,
          background: info.has_pass ? '#dcfce7' : '#fef3c7', color: info.has_pass ? '#15803d' : '#92400e' }}>
          {info.has_pass ? 'Passwort gesetzt' : 'kein Passwort'}
        </span>
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
        Quelle aktuell: {info.source === 'db' ? 'Admin (DB)' : info.source === 'env' ? 'Server-.env (Alt)' : 'nicht gesetzt'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
        <Field label="E-Mail-Adresse" type="email" placeholder="z. B. support@voogo.de" value={draft.user} onChange={set('user')} autoComplete="off" />
        <label style={{ display: 'block' }}>
          <span style={lbl}><Lock size={11} style={{ verticalAlign: -1 }} /> Passwort / App-Passwort</span>
          <input style={inp} type="password" autoComplete="new-password"
            placeholder={info.has_pass ? '•••••••• (gesetzt — leer lassen zum Behalten)' : 'Passwort eingeben'}
            value={draft.password} onChange={set('password')} />
        </label>
        <Field label="Absender-Name/-Adresse (optional)" placeholder={draft.user || 'z. B. VOOGO Support'} value={draft.from} onChange={set('from')} autoComplete="off" />
      </div>

      <button onClick={() => setAdv(a => !a)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 14, background: 'none', border: 'none', color: '#4b5563', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
        {adv ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Erweitert (Server / Port)
      </button>
      {adv && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginTop: 12 }}>
          <label><span style={lbl}>SMTP-Host</span><input style={smallInp} placeholder="smtp.ionos.de" value={draft.smtp_host} onChange={set('smtp_host')} /></label>
          <label><span style={lbl}>SMTP-Port</span><input style={smallInp} placeholder="587" value={draft.smtp_port} onChange={set('smtp_port')} /></label>
          {meta.imap && <label><span style={lbl}>IMAP-Host</span><input style={smallInp} placeholder="imap.ionos.de" value={draft.imap_host} onChange={set('imap_host')} /></label>}
          {meta.imap && <label><span style={lbl}>IMAP-Port</span><input style={smallInp} placeholder="993" value={draft.imap_port} onChange={set('imap_port')} /></label>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 18, flexWrap: 'wrap' }}>
        <button onClick={save} disabled={saving} style={{ ...btn('#0a0a0a'), opacity: saving ? 0.6 : 1 }}>
          {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Speichern
        </button>
        <button onClick={test} disabled={testing} style={{ ...btn('#9c27b0'), opacity: testing ? 0.6 : 1 }}>
          {testing ? <Loader2 size={14} className="spin" /> : <ShieldCheck size={14} />} Verbindung testen
        </button>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Test nutzt die zuletzt <b>gespeicherten</b> Daten.</span>
      </div>

      {result && (
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, fontSize: 13,
          background: result.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${result.ok ? '#bbf7d0' : '#fecaca'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 800, color: result.ok ? '#15803d' : '#b91c1c' }}>
            {result.ok ? <CheckCircle size={16} /> : <XCircle size={16} />} {result.ok ? 'Verbindung erfolgreich' : 'Verbindung fehlgeschlagen'}
          </div>
          <div style={{ marginTop: 6, color: '#374151', display: 'grid', gap: 3 }}>
            {result.smtp && <div>SMTP (Versand): {result.smtp.ok ? '✅ ok' : `❌ ${result.smtp.detail || 'Fehler'}`}</div>}
            {result.imap && <div>IMAP (Empfang): {result.imap.ok ? '✅ ok' : `❌ ${result.imap.detail || 'Fehler'}`}</div>}
            {!result.smtp && !result.imap && result.detail && <div>{result.detail}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

const META = {
  support: { key: 'support', title: 'Support-Postfach', sub: 'Empfang (Tickets) + Versand (Reset-Codes, Antworten)', icon: Mail, color: '#0a0a0a', imap: true },
  billing: { key: 'billing', title: 'Rechnung / Belege', sub: 'Versand von Belegen & Rechnungen (fällt auf Support zurück, wenn leer)', icon: Receipt, color: '#9c27b0', imap: false },
};

export default function MailAccountsPanel() {
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => api.get('/admin/mail-accounts')
    .then(r => { setAccounts(r.data.accounts); setLoading(false); setError(false); })
    .catch(() => { setLoading(false); setError(true); });
  useEffect(() => { load(); }, []);

  if (loading) return <div style={cardStyle}><Skeleton h={20} w="40%" /><div style={{ height: 12 }} /><Skeleton h={90} /></div>;
  if (error || !accounts) return <div style={cardStyle}>Konnte Mail-Konten nicht laden. <button onClick={load} style={{ ...btn('#4b5563'), padding: '6px 12px', marginLeft: 8 }}>Erneut</button></div>;

  return (
    <div>
      <div style={{ ...cardStyle, background: '#faf5ff', border: '1px solid #e9d5ff', padding: 16 }}>
        <div style={{ fontSize: 13, color: '#6b21a8', fontWeight: 700, marginBottom: 3 }}>🔒 Sicher gespeichert</div>
        <div style={{ fontSize: 13, color: '#374151' }}>Passwörter werden <b>verschlüsselt</b> gespeichert und nie wieder angezeigt. Leeres Passwortfeld = bisheriges behalten. Nach dem Speichern <b>„Verbindung testen"</b> klicken.</div>
      </div>
      <AccountCard meta={META.support} info={accounts.support} onSaved={load} />
      <AccountCard meta={META.billing} info={accounts.billing} onSaved={load} />
    </div>
  );
}
